import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import PressableWithDelay from 'shared/components/PressableWithDelay/PressableWithDelay';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import TitleSmText from 'shared/components/vxl/typography/primary/title/TitleSmText';
import theme from 'shared/styles/theme';

export interface NavBarProps {
  title: string | undefined;
  // TODO: How can we control what components are allowed here?
  primaryIcon?: React.ReactNode;
  primaryOnPress?: () => void;
  /** Renders the primary action but blocks presses, for states where going back is temporarily unsafe. */
  isPrimaryDisabled?: boolean;
  trailingAccessories?: React.ReactNode;
  // TODO: How can we control what components are allowed here?
  accessory?: React.ReactNode;
  transparent?: boolean;
  color?: string;
  // empty NavigationBar is used on MyVaroScreen, MoveMoneyScreen, and CashbackHomeScreen
  // to add padding to the top of the screen but not show anything else
  empty?: boolean;
  lightText?: boolean;
}

export function NavigationBar({
  title,
  primaryIcon = <Icon iconKey="BACK" iconSize="MD" />,
  primaryOnPress,
  isPrimaryDisabled = false,
  trailingAccessories,
  accessory,
  transparent,
  color,
  empty,
  lightText,
}: NavBarProps) {
  const { top } = useSafeAreaInsets();

  return (
    <View
      style={[
        styles.container,
        transparent ? styles.transparent : null,
        color ? { backgroundColor: color } : null,
        { paddingTop: !empty ? top + theme.spacing.m : top },
      ]}
    >
      <View style={styles.navContainer}>
        {primaryIcon && primaryOnPress ? (
          <PressableWithDelay
            role="button"
            onPress={primaryOnPress}
            disabled={isPrimaryDisabled}
            accessibilityState={{ disabled: isPrimaryDisabled }}
            testID="back-icon"
          >
            {primaryIcon}
          </PressableWithDelay>
        ) : null}
        <TitleSmText
          style={[styles.pageTitle, lightText ? styles.lightText : null]}
          testID="nav-title"
          numberOfLines={1}
          ellipsizeMode="tail"
        >
          {title}
        </TitleSmText>
        {trailingAccessories ? (
          <View style={styles.trailingAccessories}>{trailingAccessories}</View>
        ) : null}
      </View>
      {accessory ? (
        <View style={styles.accessoryContainer}>{accessory}</View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  accessoryContainer: {
    flexGrow: 0,
    flexShrink: 0,
    marginTop: theme.spacing.s,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flexDirection: 'column',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.m,
    paddingVertical: 12,
    width: '100%',
    zIndex: 1,
  },
  lightText: { color: theme.color.text.inverse },
  navContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing.m,
  },
  pageTitle: {
    color: theme.color.text.primary,
    flex: 1,
  },
  trailingAccessories: {
    marginLeft: 'auto',
  },
  transparent: {
    backgroundColor: 'transparent',
  },
});
